import { FormEvent, useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { api, ApiError } from "../../lib/api.js";
import Drawer from "../../components/Drawer.js";

type Provider = Awaited<ReturnType<typeof api.paymentProviders>>["providers"][number];

export default function AdminPaymentGateways() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [openProvider, setOpenProvider] = useState<Provider | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; message: string }>>({});

  const load = () => {
    setLoading(true);
    api.paymentProviders()
      .then((d) => setProviders(d.providers ?? []))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openEditor = (p: Provider) => {
    setOpenProvider(p);
    const initial: Record<string, string> = {};
    for (const f of p.fields) initial[f.key] = "";
    setForm(initial);
  };

  const onSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!openProvider) return;
    setSaving(true);
    setError(null);
    try {
      const fields: Record<string, string> = {};
      for (const [k, v] of Object.entries(form)) {
        if (v && v.length > 0) fields[k] = v;
      }
      if (Object.keys(fields).length === 0) {
        setError("Supply at least one credential field.");
        setSaving(false);
        return;
      }
      await api.setPaymentProviderCredentials(openProvider.name, fields);
      setBanner(`Updated credentials for ${openProvider.displayName}.`);
      setOpenProvider(null);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const onClear = async (p: Provider) => {
    if (!confirm(`Clear stored credentials for ${p.displayName}? Environment variables (if any) will still apply as fallback.`)) return;
    try {
      await api.clearPaymentProviderCredentials(p.name);
      setBanner(`Cleared stored credentials for ${p.displayName}.`);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onToggle = async (p: Provider) => {
    try {
      await api.togglePaymentProvider(p.name, !p.enabled);
      setBanner(`${p.enabled ? "Disabled" : "Enabled"} ${p.displayName}.`);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onTest = async (p: Provider) => {
    setTesting(p.name);
    try {
      const r = await api.testPaymentProvider(p.name);
      setTestResult((prev) => ({ ...prev, [p.name]: { ok: r.ok, message: r.message } }));
    } catch (e) {
      setTestResult((prev) => ({
        ...prev,
        [p.name]: { ok: false, message: e instanceof ApiError ? e.message : String(e) }
      }));
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Payment gateways</h1>
          <p>Configure and activate payment providers. Credentials are stored encrypted in the platform vault.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}

      {loading ? <SkeletonTable rows={5} columns={4} /> : (
        <div className="card-grid">
          {providers.map((p) => {
            const result = testResult[p.name];
            return (
              <section key={p.name} className="card">
                <div className="card-head" style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <div>
                    <strong>{p.displayName}</strong>
                    <div className="soft" style={{ fontSize: 12 }}>{p.description}</div>
                  </div>
                  <div>
                    {p.enabled ? <span className="pill pill-ok">Enabled</span> :
                     p.configured ? <span className="pill pill-warn">Disabled</span> :
                     <span className="pill pill-muted">Not configured</span>}
                  </div>
                </div>
                <div className="card-body">
                  <div className="soft" style={{ fontSize: 12, marginBottom: 8 }}>
                    <strong>Required:</strong>{" "}
                    {p.fields.filter((f) => f.required).map((f) => (
                      <span key={f.key} style={{ marginRight: 8 }}>
                        {f.label} {f.present ? "✓" : "✗"}
                      </span>
                    ))}
                  </div>
                  {p.missingRequired.length > 0 && (
                    <div className="alert alert-warn" style={{ fontSize: 13 }}>
                      Missing: {p.missingRequired.join(", ")}
                    </div>
                  )}
                  {result && (
                    <div className={`alert ${result.ok ? "alert-ok" : "alert-warn"}`} style={{ fontSize: 13 }}>
                      {result.message}
                    </div>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <button className="btn btn-sm btn-primary" type="button" onClick={() => openEditor(p)}>Configure</button>
                    <button
                      className="btn btn-sm"
                      type="button"
                      disabled={!p.configured}
                      onClick={() => onToggle(p)}
                    >
                      {p.enabled ? "Disable" : "Enable"}
                    </button>
                    <button
                      className="btn btn-sm"
                      type="button"
                      disabled={testing === p.name}
                      onClick={() => onTest(p)}
                    >
                      {testing === p.name ? "Testing…" : "Test"}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      type="button"
                      disabled={!p.configured}
                      onClick={() => onClear(p)}
                    >
                      Clear vault
                    </button>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}

      <Drawer
        open={openProvider != null}
        title={openProvider ? `Configure ${openProvider.displayName}` : ""}
        subtitle={openProvider?.description}
        onClose={() => setOpenProvider(null)}
        width={560}
        asForm
        onSubmit={onSave}
        footer={(
          <>
            <button type="button" className="btn" onClick={() => setOpenProvider(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save credentials"}
            </button>
          </>
        )}
      >
        {openProvider && (
          <>
            <p className="muted">
              Leave a field blank to keep its current stored value or fall back to the environment variable.
            </p>
            {openProvider.fields.map((f) => (
              <div key={f.key} className="field">
                <label>
                  {f.label}
                  {f.required && <span style={{ color: "var(--err)" }}> *</span>}
                  {f.present && <span className="pill pill-muted" style={{ marginLeft: 8, fontSize: 11 }}>stored</span>}
                </label>
                {f.options ? (
                  <select
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  >
                    <option value="">(keep current)</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    type={f.secret ? "password" : "text"}
                    autoComplete={f.secret ? "new-password" : "off"}
                    value={form[f.key] ?? ""}
                    placeholder={f.placeholder ?? (f.present ? "(stored — leave blank to keep)" : "")}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                )}
                <small className="muted"><code>{f.key}</code></small>
              </div>
            ))}
          </>
        )}
      </Drawer>
    </div>
  );
}
